import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { journeyApi, apiError } from "@/lib/journeyApi";
import {
  IncomeTypeStep, IncomeDetailsStep, EssentialsStep, LivingStep, ProtectionStep, DebtsStep,
  buildPayload, foundationToForm, blankEssential, blankDebt, num,
} from "@/components/onboarding/OnboardingSteps";

const SECTIONS = {
  income: "Income",
  essentials: "Essentials",
  living: "Living Bucket",
  protection: "Protection",
  debts: "Debts",
};

export default function FoundationEdit() {
  const { section } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fromParam = params.get("from") === "profile" ? "profile" : null;
  const from = fromParam === "profile" ? "/dashboard/profile" : "/dashboard";
  const SECTION_ORDER = Object.keys(SECTIONS);
  const curIndex = SECTION_ORDER.indexOf(section);
  const nextSection = curIndex >= 0 && curIndex < SECTION_ORDER.length - 1 ? SECTION_ORDER[curIndex + 1] : null;
  const nextUrl = nextSection ? `/dashboard/foundation/${nextSection}${fromParam ? `?from=${fromParam}` : ""}` : null;
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!(section in SECTIONS)) { navigate(from, { replace: true }); return; }
    let cancelled = false;
    journeyApi.myFoundation()
      .then((d) => {
        if (cancelled) return;
        if (!d.foundation) { navigate("/onboarding", { replace: true }); return; }
        setForm(foundationToForm(d.foundation));
      })
      .catch(() => toast.error("Could not load your foundation."));
    return () => { cancelled = true; };
  }, [section, navigate, from]);

  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "auto" }); }, [section]);

  // Setters (same shape as onboarding orchestrator)
  const setIncome = useCallback((patch) => setForm((f) => ({ ...f, income: { ...f.income, ...patch } })), []);
  const setSafety = useCallback((patch) => setForm((f) => ({ ...f, safety: { ...f.safety, ...patch } })), []);
  const setLiving = useCallback((v) => setForm((f) => ({ ...f, living_bucket: v })), []);
  const setMortgage = useCallback((patch) => setForm((f) => ({ ...f, mortgage: { ...(f.mortgage || {}), ...patch } })), []);
  const addEssential = useCallback(() => setForm((f) => ({ ...f, essentials: [...f.essentials, blankEssential()] })), []);
  const updateEssential = useCallback((id, patch) => setForm((f) => ({ ...f, essentials: f.essentials.map((e) => (e.id === id ? { ...e, ...patch } : e)) })), []);
  const removeEssential = useCallback((id) => setForm((f) => ({ ...f, essentials: f.essentials.filter((e) => e.id !== id) })), []);
  const addDebt = useCallback(() => setForm((f) => ({ ...f, debts: [...f.debts, blankDebt()] })), []);
  const updateDebt = useCallback((id, patch) => setForm((f) => ({ ...f, debts: f.debts.map((d) => (d.id === id ? { ...d, ...patch } : d)) })), []);
  const removeDebt = useCallback((id) => setForm((f) => ({ ...f, debts: f.debts.filter((d) => d.id !== id) })), []);

  const save = async (dest) => {
    if (section === "income" && !form.income.type) return toast.error("Choose how your income shows up.");
    if (section === "income" && form.income.type !== "variable") {
      if (num(form.income.take_home_paycheck) <= 0 || !form.income.pay_frequency) {
        return toast.error("Enter your take-home amount and pay frequency.");
      }
    }
    setSaving(true);
    try {
      await journeyApi.updateFoundation(buildPayload(form));
      toast.success("Foundation updated.");
      navigate(dest);
    } catch (e) {
      toast.error(apiError(e, "Could not save your changes."));
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return (
      <DashboardShell>
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#E5E0D9] border-t-[#2F6B57]" />
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell>
      <button onClick={() => navigate(from)} data-testid="foundation-edit-back" className="mb-4 inline-flex items-center gap-1.5 text-sm text-[#70584A] hover:text-[#164C3B]">
        <ArrowLeft className="h-4 w-4" /> {from === "/dashboard/profile" ? "Back to profile" : "Back to dashboard"}
      </button>
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#C9A86A]">Edit foundation</p>
      <h1 className="mb-6 mt-1 font-serif text-3xl text-[#171717] sm:text-4xl" data-testid="foundation-edit-title">{SECTIONS[section]}</h1>

      <div data-testid={`foundation-edit-${section}`}>
        {section === "income" && (
          <div className="space-y-8">
            <IncomeTypeStep form={form} setIncome={setIncome} />
            <IncomeDetailsStep form={form} setIncome={setIncome} />
          </div>
        )}
        {section === "essentials" && <EssentialsStep form={form} addEssential={addEssential} updateEssential={updateEssential} removeEssential={removeEssential} setMortgage={setMortgage} />}
        {section === "living" && <LivingStep form={form} setLiving={setLiving} />}
        {section === "protection" && <ProtectionStep form={form} setIncome={setIncome} setSafety={setSafety} />}
        {section === "debts" && <DebtsStep form={form} addDebt={addDebt} updateDebt={updateDebt} removeDebt={removeDebt} />}
      </div>

      <div className="mt-8 flex flex-col gap-3 border-t border-[#E5E0D9] pt-5 sm:flex-row sm:items-center sm:justify-end">
        <Button variant="ghost" onClick={() => navigate(from)} data-testid="foundation-cancel" className="min-h-11 rounded-xl text-[#70584A] hover:bg-[#F5F2EC] sm:order-1">Cancel</Button>
        <Button variant="outline" onClick={() => save(from)} disabled={saving} data-testid="foundation-save" className="min-h-11 rounded-xl border-[#2F6B57] px-6 font-semibold text-[#164C3B] hover:bg-[#F5F2EC] sm:order-2">
          {saving ? "Saving…" : "Save changes"}
        </Button>
        {nextUrl ? (
          <Button onClick={() => save(nextUrl)} disabled={saving} data-testid="foundation-save-next" className="min-h-11 rounded-xl bg-[#2F6B57] px-6 font-semibold text-white hover:bg-[#255643] sm:order-3">
            {saving ? "Saving…" : "Save & Next"}
          </Button>
        ) : (
          <Button onClick={() => save(from)} disabled={saving} data-testid="foundation-save-return" className="min-h-11 rounded-xl bg-[#2F6B57] px-6 font-semibold text-white hover:bg-[#255643] sm:order-3">
            {saving ? "Saving…" : (fromParam === "profile" ? "Save & Return to Profile" : "Save & Finish")}
          </Button>
        )}
      </div>
    </DashboardShell>
  );
}
